/**
 * Billing Cycle Service
 * Manages subscription billing cycles (monthly/yearly) and renewal dates
 */

import { supabase } from './supabase'

/**
 * Get organization subscription with billing cycle info
 */
export const getOrganizationSubscription = async (organizationId) => {
  try {
    // Try new organization_subscriptions table first
    const { data: subscription, error: subError } = await supabase
      .from('organization_subscriptions')
      .select(`
        *,
        tier:subscription_tiers(*)
      `)
      .eq('organization_id', organizationId)
      .maybeSingle()

    if (!subError && subscription) {
      return { success: true, data: subscription }
    }

    // Fallback to old subscriptions table
    const { data: oldSub, error: oldError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (oldError && oldError.code !== 'PGRST116') {
      throw oldError
    }

    if (oldSub) {
      // Convert old subscription format to new format
      return {
        success: true,
        data: {
          ...oldSub,
          billing_cycle: 'monthly', // Default for old subscriptions
          current_period_start: oldSub.starts_at,
          current_period_end: oldSub.ends_at,
        },
      }
    }

    return { success: true, data: null }
  } catch (error) {
    console.error('Error getting organization subscription:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Calculate next renewal date based on billing cycle
 */
export const calculateNextRenewalDate = (currentPeriodEnd, billingCycle) => {
  if (!currentPeriodEnd) {
    // If no current period end, calculate from now
    const nextDate = new Date()
    if (billingCycle === 'yearly') {
      nextDate.setFullYear(nextDate.getFullYear() + 1)
    } else {
      nextDate.setMonth(nextDate.getMonth() + 1)
    }
    return nextDate
  }

  const renewalDate = new Date(currentPeriodEnd)
  if (billingCycle === 'yearly') {
    renewalDate.setFullYear(renewalDate.getFullYear() + 1)
  } else {
    renewalDate.setMonth(renewalDate.getMonth() + 1)
  }
  return renewalDate
}

/**
 * Get billing cycle information including pricing
 */
export const getBillingCycleInfo = async (organizationId) => {
  try {
    // Get organization and subscription
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .select('subscription_tier')
      .eq('id', organizationId)
      .single()

    if (orgError) throw orgError

    const subResult = await getOrganizationSubscription(organizationId)
    if (!subResult.success) throw new Error(subResult.error)

    const subscription = subResult.data

    // Get tier pricing
    let tierQuery = supabase
      .from('subscription_tiers')
      .select('*, metadata')
    
    // Build query conditionally - only include id.eq if tier_id exists
    if (subscription?.tier_id) {
      tierQuery = tierQuery.or(`code.eq.${org.subscription_tier},id.eq.${subscription.tier_id}`)
    } else {
      tierQuery = tierQuery.eq('code', org.subscription_tier)
    }
    
    const { data: tier, error: tierError } = await tierQuery.maybeSingle()

    if (tierError && tierError.code !== 'PGRST116') {
      console.warn('Error fetching tier pricing:', tierError)
    }

    const monthlyPrice = tier?.metadata?.zar_price_monthly || tier?.price_monthly || 0
    const yearlyPrice = tier?.metadata?.zar_price_yearly || tier?.price_yearly || 0

    const currentCycle = subscription?.billing_cycle || 'monthly'
    const currentPeriodStart = subscription?.current_period_start
      ? new Date(subscription.current_period_start)
      : new Date()
    const currentPeriodEnd = subscription?.current_period_end
      ? new Date(subscription.current_period_end)
      : calculateNextRenewalDate(null, currentCycle)

    const nextRenewalDate = calculateNextRenewalDate(currentPeriodEnd, currentCycle)

    return {
      success: true,
      data: {
        currentCycle,
        currentPeriodStart,
        currentPeriodEnd,
        nextRenewalDate,
        monthlyPrice,
        yearlyPrice,
        currency: tier?.currency || 'ZAR',
        tier: tier,
      },
    }
  } catch (error) {
    console.error('Error getting billing cycle info:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update billing cycle for an organization
 * Changes take effect at the end of the current billing period
 */
export const updateBillingCycle = async (organizationId, newCycle, effectiveDate = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('User not authenticated')

    // Get current subscription
    const subResult = await getOrganizationSubscription(organizationId)
    if (!subResult.success) throw new Error(subResult.error)

    const subscription = subResult.data

    if (!subscription) {
      throw new Error('No active subscription found')
    }

    // Use provided effective date or current period end
    const effectiveAt = effectiveDate
      ? new Date(effectiveDate)
      : subscription.current_period_end
      ? new Date(subscription.current_period_end)
      : new Date()

    // Calculate new period end based on new cycle
    const newPeriodEnd = calculateNextRenewalDate(effectiveAt, newCycle)

    // Update organization_subscriptions table
    const { data: updatedSub, error: updateError } = await supabase
      .from('organization_subscriptions')
      .update({
        billing_cycle: newCycle,
        current_period_end: newPeriodEnd.toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (updateError) {
      // If organization_subscriptions doesn't exist, create it
      if (updateError.code === 'PGRST116') {
        // Get tier_id from subscription_tiers
        const { data: org } = await supabase
          .from('organizations')
          .select('subscription_tier')
          .eq('id', organizationId)
          .single()

        if (org?.subscription_tier) {
          const { data: tier } = await supabase
            .from('subscription_tiers')
            .select('id')
            .eq('code', org.subscription_tier)
            .single()

          if (tier) {
            const { data: newSub, error: createError } = await supabase
              .from('organization_subscriptions')
              .insert({
                organization_id: organizationId,
                tier_id: tier.id,
                billing_cycle: newCycle,
                status: 'active',
                current_period_start: effectiveAt.toISOString(),
                current_period_end: newPeriodEnd.toISOString(),
              })
              .select()
              .single()

            if (createError) throw createError
            return { success: true, data: newSub }
          }
        }
      }
      throw updateError
    }

    return { success: true, data: updatedSub }
  } catch (error) {
    console.error('Error updating billing cycle:', error)
    return { success: false, error: error.message }
  }
}

